import React, { useEffect, useState } from 'react';
import { Button, Input, Form, Tooltip, message } from 'antd';
import { EfaxModal } from './styles';
import { updateEFaxUser, addEfaxUser } from '../../utils/services/efaxAPI';
import { withTranslation } from 'react-i18next';
import { EFAX_CONFIGURATION_MODAL as trans } from '../../containers/UserSettingsPage/TranslationValues';

const NAMESPACE = ['EFAX_CONFIGURATION_MODAL'];

const EFaxForm = props => {
  const { visible, onCancel, targetUser, isEditing, t } = props;
  const { getFieldDecorator, setFieldsValue } = props.form;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditing) {
      const { configName, apiId, apiSecret, userId, faxNumber } = targetUser;

      setFieldsValue({
        configName: configName,
        faxNumber: faxNumber,
        apiId: apiId,
        userId: userId,
        apiSecret: apiSecret,
      });
    }
  }, []);

  const validations = [
    {
      required: true,
      message: t(trans.FIELD_REQUIRED_ERROR_MESSAGE.key, trans.FIELD_REQUIRED_ERROR_MESSAGE.value),
    },
    {
      pattern: /^(?=\S)(?!\s+$).*/,
      message: t(
        trans.BEGINNING_SPACE_ERROR_MESSAGE.key,
        trans.BEGINNING_SPACE_ERROR_MESSAGE.value,
      ),
    },
  ];

  const hasChanged = (json1, json2) => {
    for (const key in json2) {
      if (json1[key] !== json2[key]) {
        return true;
      }
    }
    return false;
  };

  const handleInputChange = fieldName => e => {
    setFieldsValue({ [fieldName]: e.target.value });
  };

  const handleSubmit = e => {
    e.preventDefault();
    props.form.validateFields(async (err, values) => {
      if (!err) {
        if (isEditing) {
          const isModified = hasChanged(targetUser, values);
          if (!isModified) {
            message.warning(
              t(
                trans.NO_EDITS_PERFORMED_WARNING_MESSAGE.key,
                trans.NO_EDITS_PERFORMED_WARNING_MESSAGE.value,
              ),
            );
            return;
          }
        }
        setLoading(true);
        let response = {};
        if (isEditing) {
          const { id } = targetUser;
          response = await updateEFaxUser({ ...values, id: id });
          if (response && response.statusCode === 669) {
            message.success(
              t(trans.UPDATED_SUCCESS_MESSAGE.key, trans.UPDATED_SUCCESS_MESSAGE.value),
            );
            onCancel(true);
          } else {
            message.error(
              t(trans.UPDATE_FAILED_ERROR_MESSAGE.key, trans.UPDATE_FAILED_ERROR_MESSAGE.value),
            );
          }
        } else {
          response = await addEfaxUser(values);
          if (response && response.statusCode === 667) {
            message.success(t(trans.ADDED_SUCCESS_MESSAGE.key, trans.ADDED_SUCCESS_MESSAGE.value));
            onCancel(true);
          } else {
            message.error(
              t(trans.ADD_FAILED_ERROR_MESSAGE.key, trans.ADD_FAILED_ERROR_MESSAGE.value),
            );
          }
        }
        setLoading(false);
      }
    });
  };

  const LabelWithHelpIcon = ({ label, helpText }) => {
    return (
      <>
        <span>{label}</span>
        <span className={'label-colon'}>:</span>
        <Tooltip placement="top" title={helpText}>
          <i className="fa-regular fa-circle-question label-help-icon"></i>
        </Tooltip>
      </>
    );
  };

  return (
    <div onKeyDown={e => e.stopPropagation()}>
      <EfaxModal
        title={
          isEditing
            ? t(
                trans.UPDATE_FAX_CONFIGURATION_TITLE.key,
                trans.UPDATE_FAX_CONFIGURATION_TITLE.value,
              )
            : t(trans.ADD_FAX_CONFIGURATION_TITLE.key, trans.ADD_FAX_CONFIGURATION_TITLE.value)
        }
        visible={visible}
        width={550}
        onCancel={() => onCancel(false)}
        maskClosable={false}
        closable={true}
        centered
        footer={[
          <Button loading={loading} type="primary" onClick={handleSubmit}>
            {isEditing
              ? loading
                ? t(trans.UPDATING_BUTTON.key, trans.UPDATING_BUTTON.value)
                : t(trans.UPDATE_BUTTON.key, trans.UPDATE_BUTTON.value)
              : loading
              ? t(trans.ADDING_BUTTON.key, trans.ADDING_BUTTON.value)
              : t(trans.ADD_BUTTON.key, trans.ADD_BUTTON.value)}
          </Button>,
        ]}
      >
        <Form>
          <Form.Item
            label={t(
              trans.UNIQUE_CONFIGURATION_NAME_LABEL.key,
              trans.UNIQUE_CONFIGURATION_NAME_LABEL.value,
            )}
          >
            {getFieldDecorator('configName', {
              rules: validations,
            })(<Input size="large" onChange={handleInputChange} autoFocus />)}
          </Form.Item>

          <Form.Item label={t(trans.FAX_NUMBER_LABEL.key, trans.FAX_NUMBER_LABEL.value)}>
            {getFieldDecorator('faxNumber', {
              rules: [
                ...validations,
                {
                  pattern: /^\+?\d+$/,
                  message: t(
                    trans.FAX_NUMBER_NOT_VALID_ERROR_MESSAGE.key,
                    trans.FAX_NUMBER_NOT_VALID_ERROR_MESSAGE.value,
                  ),
                },
              ],
            })(<Input size="large" onChange={handleInputChange} />)}
          </Form.Item>

          <Form.Item
            label={
              <LabelWithHelpIcon
                label={t(trans.API_ID_LABEL.key, trans.API_ID_LABEL.value)}
                helpText={t(trans.API_ID_HELP_TEXT.key, trans.API_ID_HELP_TEXT.value)}
              />
            }
            colon={false}
          >
            {getFieldDecorator('apiId', {
              rules: validations,
            })(<Input size="large" onChange={handleInputChange} />)}
          </Form.Item>

          <Form.Item
            label={
              <LabelWithHelpIcon
                label={t(trans.USER_ID_LABEL.key, trans.USER_ID_LABEL.value)}
                helpText={t(trans.USER_ID_HELP_TEXT.key, trans.USER_ID_HELP_TEXT.value)}
              />
            }
            colon={false}
          >
            {getFieldDecorator('userId', {
              rules: validations,
            })(<Input size="large" onChange={handleInputChange} />)}
          </Form.Item>

          <Form.Item label={t(trans.API_KEY_LABEL.key, trans.API_KEY_LABEL.value)}>
            {getFieldDecorator('apiSecret', {
              rules: validations,
            })(<Input size="large" onChange={handleInputChange} />)}
          </Form.Item>
        </Form>
      </EfaxModal>
    </div>
  );
};

const EfaxConfigModal = Form.create({ name: 'efax_form' })(EFaxForm);
export default withTranslation(NAMESPACE)(EfaxConfigModal);
